// Upload caps shared by the client-side pre-check and the server's 400. Kept
// here because a drifted pair is the worst version of this: the picker accepts a
// file, the whole envelope uploads, and only then does the server reject it.
export const MAX_UPLOAD_FILE_SIZE = 10 * 1024 * 1024 // 10 MB

// One envelope is one or a few scans. Every file is fully buffered in memory
// (multipart parse + arrayBuffer copy + per-page split copies), so the count cap
// — together with the per-file size cap — bounds a request's heap.
export const MAX_SCAN_IMPORT_FILES = 20

// Ceilings on one generated document package (broker MC packet / compiled driver
// file). `uploaded_files` records no size, so the byte ceiling can only be
// enforced cumulatively as the merge reads — but between them these bound the
// merge's peak instead of letting it scale with however many trucks and drivers
// an application declares. Generating the package OOM-killed the pod three times
// before it was bounded at all (PET-536, PET-3815).
export const MAX_PACKAGE_ATTACHMENTS = 200
export const MAX_PACKAGE_MERGED_BYTES = 350 * 1024 * 1024 // 350 MB

// How long an unfinished package job is believed before it is treated as
// abandoned. The active partial unique means a `pending` row blocks every
// re-enqueue for its slot, so a job that never got picked up (Redis flushed,
// worker down through a deploy) would otherwise disable the Generate button
// permanently — the one state with no way out from the UI.
//
// It is a deadline on *silence*, not on total merge duration: the worker bumps
// `updatedAt` per attachment (`PACKAGE_JOB_HEARTBEAT_MS`), so a merge that runs
// longer than this stays alive as long as it is still making progress. That
// matters because the same window is also the BullMQ `lockDuration` — one number
// deciding "abandoned" in three places, and a merge of a 20-vehicle application
// has not been timed.
export const PACKAGE_JOB_STALE_AFTER_MS = 15 * 60 * 1000

// How often the merge is allowed to touch its job row. Liveness, not a progress
// count — every beat is a round trip, and attachments can be small and many.
// Beats come per attachment plus once for the tail, so the one stretch it
// cannot cover is `save()` itself, which is synchronous.
export const PACKAGE_JOB_HEARTBEAT_MS = 30 * 1000

export const ACCEPTED_SCAN_MIME_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/heic',
] as const

/** The `accept` attribute value matching ACCEPTED_SCAN_MIME_TYPES. */
export const ACCEPTED_SCAN_ACCEPT_ATTR = ACCEPTED_SCAN_MIME_TYPES.join(',')

// Photos only — no PDF — for facility-material image galleries (PET-3872).
export const ACCEPTED_PHOTO_MIME_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/heic',
] as const

/** The `accept` attribute value matching ACCEPTED_PHOTO_MIME_TYPES. */
export const ACCEPTED_PHOTO_ACCEPT_ATTR = ACCEPTED_PHOTO_MIME_TYPES.join(',')

// Ceiling on a facility material's whole photo gallery, enforced cumulatively
// across uploads. It also sizes the upload route's body cap
// (MAX_UPLOAD_FILE_SIZE * this), so it must stay well under the global
// MAX_REQUEST_BODY_SIZE backstop or that cap bounds nothing.
export const MAX_PHOTOS_PER_MATERIAL = 10
